/**
 * Checks if a string is a valid UUID format
 */
function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Generates a SEO-friendly slug from a product title
 */
export function generateProductSlug(title: string): string {
  if (!title) return '';
  
  return title
    .toLowerCase()
    // Replace Albanian characters
    .replace(/ë/g, 'e')
    .replace(/ç/g, 'c')
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    // Remove special characters and replace with dash
    .replace(/[^a-z0-9\s-]/g, '')
    // Replace spaces and multiple dashes with single dash
    .replace(/[\s-]+/g, '-')
    // Remove leading/trailing dashes
    .replace(/^-+|-+$/g, '')
    // Limit length to 50 characters
    .substring(0, 50)
    .replace(/-+$/, ''); // Remove trailing dash if cut off
}

/**
 * Creates a complete SEO URL for a product
 */
export function createProductUrl(id: string | number, title: string): string {
  const slug = generateProductSlug(title);
  return slug ? `/product/${id}-${slug}` : `/product/${id}`;
}

/**
 * Extracts ID from a product URL that may contain a slug
 * Handles both UUIDs and numeric IDs
 */
export function extractProductId(idOrIdSlug: string): string {
  // If it's a UUID (contains hyphens in UUID format), check if it has a slug
  if (idOrIdSlug.includes('-')) {
    // Try to extract a UUID from the beginning
    const possibleUUID = idOrIdSlug.substring(0, 36); // UUID is 36 characters
    if (isUUID(possibleUUID)) {
      return possibleUUID;
    }
    
    // If not a UUID, treat as numeric ID with slug - take everything before first dash
    const dashIndex = idOrIdSlug.indexOf('-');
    return dashIndex > 0 ? idOrIdSlug.substring(0, dashIndex) : idOrIdSlug;
  }
  
  // No dashes, return as is (backward compatibility)
  return idOrIdSlug;
}

/**
 * Validates if a URL matches the expected format for a product
 */
export function validateProductUrl(id: string | number, title: string, currentUrl: string): boolean {
  const expectedUrl = createProductUrl(id, title);
  const currentPath = currentUrl.split('?')[0]; // Remove query params
  const expectedPath = expectedUrl.split('?')[0];
  return currentPath === expectedPath;
}

/**
 * Generates a SEO-friendly slug from a store name
 */
export function generateStoreSlug(storeName: string): string {
  if (!storeName) return '';
  
  return storeName
    .toLowerCase()
    // Replace Albanian characters
    .replace(/ë/g, 'e')
    .replace(/ç/g, 'c')
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    // Remove special characters and replace with dash
    .replace(/[^a-z0-9\s-]/g, '')
    // Replace spaces and multiple dashes with single dash
    .replace(/[\s-]+/g, '-')
    // Remove leading/trailing dashes
    .replace(/^-+|-+$/g, '')
    // Limit length to 50 characters
    .substring(0, 50)
    .replace(/-+$/, ''); // Remove trailing dash if cut off
}

/**
 * Creates a complete SEO URL for a store
 */
export function createStoreUrl(id: string | number, storeName: string): string {
  const slug = generateStoreSlug(storeName);
  return slug ? `/store/${id}-${slug}` : `/store/${id}`;
}

/**
 * Extracts ID from a store URL that may contain a slug
 * Handles both UUIDs and numeric IDs
 */
export function extractStoreId(idOrIdSlug: string): string {
  // If it's a UUID (contains hyphens in UUID format), check if it has a slug
  if (idOrIdSlug.includes('-')) {
    // Try to extract a UUID from the beginning
    const possibleUUID = idOrIdSlug.substring(0, 36); // UUID is 36 characters
    if (isUUID(possibleUUID)) {
      return possibleUUID;
    }
    
    // If not a UUID, treat as numeric ID with slug - take everything before first dash
    const dashIndex = idOrIdSlug.indexOf('-');
    return dashIndex > 0 ? idOrIdSlug.substring(0, dashIndex) : idOrIdSlug;
  }
  
  // No dashes, return as is (backward compatibility)
  return idOrIdSlug;
}

/**
 * Validates if a URL matches the expected format for a store
 */
export function validateStoreUrl(id: string | number, storeName: string, currentUrl: string): boolean {
  const expectedUrl = createStoreUrl(id, storeName);
  const currentPath = currentUrl.split('?')[0]; // Remove query params
  const expectedPath = expectedUrl.split('?')[0];
  return currentPath === expectedPath;
}

/**
 * Generates a SEO-friendly slug from a category name
 */
export function generateCategorySlug(categoryName: string): string {
  if (!categoryName) return '';
  
  return categoryName
    .toLowerCase()
    // Replace Albanian characters
    .replace(/ë/g, 'e')
    .replace(/ç/g, 'c')
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    // Remove special characters and replace with dash
    .replace(/[^a-z0-9\s-]/g, '')
    // Replace spaces and multiple dashes with single dash
    .replace(/[\s-]+/g, '-')
    // Remove leading/trailing dashes
    .replace(/^-+|-+$/g, '')
    // Limit length to 50 characters
    .substring(0, 50)
    .replace(/-+$/, ''); // Remove trailing dash if cut off
}

/**
 * Creates a complete SEO URL for a category
 */
export function createCategoryUrl(id: string | number, categoryName: string): string {
  const slug = generateCategorySlug(categoryName);
  return slug ? `/category/${id}-${slug}` : `/category/${id}`;
}

/**
 * Extracts ID from a category URL that may contain a slug
 */
export function extractCategoryId(idOrIdSlug: string): string {
  // If it contains a dash, take everything before the first dash
  if (idOrIdSlug.includes('-')) {
    const dashIndex = idOrIdSlug.indexOf('-');
    return dashIndex > 0 ? idOrIdSlug.substring(0, dashIndex) : idOrIdSlug;
  }
  
  // No dashes, return as is (backward compatibility)
  return idOrIdSlug;
}

/**
 * Validates if a URL matches the expected format for a category
 */
export function validateCategoryUrl(id: string | number, categoryName: string, currentUrl: string): boolean {
  const expectedUrl = createCategoryUrl(id, categoryName);
  const currentPath = currentUrl.split('?')[0]; // Remove query params
  const expectedPath = expectedUrl.split('?')[0];
  return currentPath === expectedPath;
}

/**
 * Creates a complete SEO URL for category search
 */
export function createSearchCategoryUrl(id: string | number, categoryName: string): string {
  const slug = generateCategorySlug(categoryName);
  return slug ? `/search/${id}-${slug}` : `/search/${id}`;
}

/**
 * Extracts ID from a search category URL that may contain a slug
 */
export function extractSearchCategoryId(idOrIdSlug: string): string {
  // If it contains a dash, take everything before the first dash
  if (idOrIdSlug.includes('-')) {
    const dashIndex = idOrIdSlug.indexOf('-');
    return dashIndex > 0 ? idOrIdSlug.substring(0, dashIndex) : idOrIdSlug;
  }
  
  // No dashes, return as is (backward compatibility)
  return idOrIdSlug;
}
